/**
 * Memory Flip drill strategy. A Memory Flip step plays a Vocabulary stack and
 * grades like Vocabulary (the flipped card's text is the answer), so the
 * strategy is Vocabulary's; only the solve card differs: a board of
 * face-down translation cards, kept for a window of questions.
 */
import type { DrillVocabulary } from 'src/types/components';
import { vocabularyStrategy, vocabularyItemCorrect } from './vocabulary';
import type { DrillItemMap, DrillStrategy } from './types';

type Item = DrillItemMap['MemoryFlip'];

export const memoryFlipStrategy: DrillStrategy<'MemoryFlip'> = {
  ...(vocabularyStrategy as unknown as DrillStrategy<'MemoryFlip'>),
  kind: 'MemoryFlip',
};

/** Cards per board when the drill sets no number_of_options. */
export const DEFAULT_BOARD_SIZE = 6;

/**
 * The board's questions: the `size`-long window of the stack (in play
 * order) that holds the current item. Items before it were answered —
 * the engine always presents the first unanswered item.
 */
export function memoryWindow(
  orderedStackSource: readonly Item[],
  currentStackItem: Item | undefined,
  size: number,
): { items: Item[]; currentIndex: number } {
  const n = Math.max(2, size);
  const index = orderedStackSource.findIndex(
    (item) => item?.id === currentStackItem?.id,
  );
  const start = index < 0 ? 0 : Math.floor(index / n) * n;
  return {
    items: orderedStackSource.slice(start, start + n),
    currentIndex: index < 0 ? -1 : index - start,
  };
}

export type MemoryCard = { id: Item['id']; text: string };

/** One shuffled translation card per window item. */
export function memoryCards(
  items: readonly Item[],
  drillType: DrillVocabulary['attributes'] | undefined,
  random: () => number = Math.random,
): MemoryCard[] {
  const cards = items
    .map((item) => ({
      id: item.id,
      text: vocabularyItemCorrect(item, drillType) ?? '',
    }))
    .filter((card) => card.text);
  for (let i = cards.length - 1; i > 0; i--) {
    const j = Math.floor(random() * (i + 1));
    [cards[i], cards[j]] = [cards[j]!, cards[i]!];
  }
  return cards;
}
